import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Switch } from "../../ui/switch";
import { nowHM, todayISO, STATUSES } from "./format";

const field = "h-11 w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 text-sm text-zinc-100 outline-none transition-colors placeholder:text-zinc-500 focus:border-gold/60 [color-scheme:dark]";
const Label = ({ children }) => <span className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-400">{children}</span>;

const blank = (meId) => ({ admin_id: meId, client_name: "", date: todayISO(), time: nowHM(), registered: false, deposited: false, purchased: false, notes: "" });

export const ClientDialog = ({ client, admins, meId, isOwner, onClose, onSave }) => {
  const open = client !== null;
  const [form, setForm] = useState(blank(meId));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (client) setForm(client.id ? { ...blank(meId), ...client } : blank(meId));
  }, [client, meId]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await onSave(form);
    setBusy(false);
    if (ok) onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="crm-client-dialog" data-lenis-prevent className="max-h-[92svh] max-w-lg overflow-y-auto rounded-[28px] border-white/10 bg-surface text-zinc-100">
        <DialogTitle className="font-display text-xl">{client?.id ? "Edit client" : "New client"}</DialogTitle>
        <DialogDescription className="text-zinc-400">Changes are saved for all three admins.</DialogDescription>
        <form onSubmit={submit} className="mt-2 space-y-4">
          <label className="block">
            <Label>Client name</Label>
            <input required maxLength={120} data-testid="crm-form-name" value={form.client_name} onChange={(e) => set("client_name", e.target.value)} placeholder="e.g. John Smith" className={field} />
          </label>
          <div>
            <Label>Admin responsible</Label>
            <Select value={form.admin_id} onValueChange={(v) => set("admin_id", v)} disabled={!isOwner}>
              <SelectTrigger className="h-11 rounded-2xl border-white/10 bg-white/[0.04] px-4 text-sm" data-testid="crm-form-admin"><SelectValue /></SelectTrigger>
              <SelectContent>
                {admins.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}{a.id === meId ? " (you)" : ""}</SelectItem>)}
              </SelectContent>
            </Select>
            {!isOwner && <p className="mt-1.5 text-[12px] text-zinc-400">Only the owner can assign clients to another admin.</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><Label>Date</Label><input type="date" required data-testid="crm-form-date" value={form.date} onChange={(e) => set("date", e.target.value)} className={field} /></label>
            <label className="block"><Label>Time</Label><input type="time" required data-testid="crm-form-time" value={form.time} onChange={(e) => set("time", e.target.value)} className={field} /></label>
          </div>
          <div className="space-y-2">
            {STATUSES.map((s) => (
              <label key={s.key} className="flex items-center justify-between rounded-2xl border border-white/5 bg-white/[0.03] px-4 py-3 text-sm text-zinc-200">
                {s.label}
                <Switch checked={form[s.key]} onCheckedChange={(v) => set(s.key, v)} data-testid={`crm-form-${s.key}`} />
              </label>
            ))}
          </div>
          <label className="block">
            <Label>Notes (optional)</Label>
            <textarea rows={3} maxLength={2000} data-testid="crm-form-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} className={`${field} h-auto py-3`} />
          </label>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} data-testid="crm-form-cancel" className="h-12 flex-1 rounded-full border border-white/10 text-sm font-semibold text-zinc-300 transition-colors hover:text-zinc-50">Cancel</button>
            <button disabled={busy} data-testid="crm-form-save" className="flex h-12 flex-1 items-center justify-center gap-2 rounded-full bg-gold text-sm font-semibold text-ink transition-opacity hover:opacity-90 disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
